import { FileText, Image as ImageIcon, Link2, List, Mic, Music, Paperclip, Rows3, Send, ShoppingBag, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { checkWhatsAppMedia } from '../../utils/whatsapp';
import { prepareForWhatsApp } from '../../utils/whatsappMediaPrep';
import InteractiveComposer from './InteractiveComposer';
import MediaPreview from './MediaPreview';
import useVoiceRecorder from './useVoiceRecorder';
import VoiceRecorderBar from './VoiceRecorderBar';

const MAX_REPLY_CHARS = 4096; // mirrors backend/routes/admin/whatsapp_inbox.py MAX_REPLY_CHARS
const WARN_AT = MAX_REPLY_CHARS - 200;

const MENU = [
  { key: 'photos', label: 'Photos & videos', accept: '.jpg,.jpeg,.png,.webp,.heic,.heif,.mp4', Icon: ImageIcon },
  { key: 'document', label: 'Document', accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt', Icon: FileText },
  { key: 'audio', label: 'Audio', accept: '.aac,.amr,.mp3,.m4a,.ogg', Icon: Music },
];

// Each opens a compose panel instead of a file picker - see interactiveMode below.
const INTERACTIVE_MENU = [
  { key: 'buttons', label: 'Quick-reply buttons', Icon: Rows3 },
  { key: 'list', label: 'List menu', Icon: List },
  { key: 'cta_url', label: 'Link button', Icon: Link2 },
  { key: 'catalog', label: 'Catalog', Icon: ShoppingBag },
];

const DisabledBar = ({ text }) => (
  <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
    <p className="text-sm text-gray-600">{text}</p>
  </div>
);

/**
 * The message bar at the bottom of a thread. Which state it shows comes entirely from the server
 * fields on the open thread (`windowOpen`, `suppressed`) plus a `send_disabled` 409, never from a
 * client-side recomputation of the 24 hour rule.
 * @param {{windowOpen: boolean, suppressed: boolean, sendDisabled: boolean, onSend: (text: string) => Promise<{ok: boolean, code: string | null, message?: string}>, phone?: string, onSendMedia?: (media: {url: string, mime_type: string, caption?: string, filename?: string}, phone?: string) => Promise<{ok: boolean, code: string | null, message?: string}>, onSendInteractive?: (payload: any) => Promise<{ok: boolean, code: string | null, message?: string}>, onSendCatalog?: (body: string) => Promise<{ok: boolean, code: string | null, message?: string}>, onOpenTemplates?: () => void, replyingTo?: {id: string, text: string} | null, onCancelReply?: () => void, editing?: {id: string, text: string} | null, onSaveEdit?: (text: string) => Promise<{ok: boolean, code: string | null, message?: string}>, onCancelEdit?: () => void}} props
 */
const Composer = ({
  phone,
  windowOpen,
  suppressed,
  sendDisabled,
  onSend,
  onSendMedia,
  onSendInteractive,
  onSendCatalog,
  onOpenTemplates,
  replyingTo,
  onCancelReply,
  editing,
  onSaveEdit,
  onCancelEdit,
}) => {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [interactiveMode, setInteractiveMode] = useState(null); // 'buttons' | 'list' | 'cta_url' | 'catalog' | null
  const [catalogBody, setCatalogBody] = useState('');
  const [catalogError, setCatalogError] = useState('');
  const menuRef = useRef(null);
  const [pending, setPending] = useState(null); // {file, serverCompress, busy}
  const [voiceBusy, setVoiceBusy] = useState(false);
  const mountedRef = useRef(true);
  const pickIdRef = useRef(0);
  const inputRefs = useRef({});
  const textareaRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Land in a chat ready to type, instead of having to click the box first.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Grows to show a multiline draft (at least 3 lines) instead of leaving it cramped in a
  // single-row box with internal scroll; collapses back to one line once empty.
  //
  // `scrollHeight` includes the box's own padding, so the "one line" baseline it's compared
  // against must include that padding too - otherwise a single empty line always reads as
  // taller than a bare line-height and the box never collapses back down (it previously didn't,
  // in every real browser; jsdom's scrollHeight is always 0, which is why the old formula's
  // tests still passed).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const paddingY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const oneLine = lineHeight + paddingY;
    const natural = el.scrollHeight;
    const isMultiline = value.includes('\n') || natural > oneLine + 1;
    const target = isMultiline
      ? Math.min(Math.max(natural, lineHeight * 3 + paddingY), lineHeight * 6 + paddingY)
      : oneLine;
    el.style.height = `${target}px`;
  }, [value]);

  // Entering edit mode replaces the draft with that message's text; keyed by id so typing doesn't
  // get stomped if this effect re-runs for an unrelated reason.
  useEffect(() => {
    if (editing) setValue(editing.text || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Uploads the file, then sends the message built from the upload RESPONSE (the server may have
  // converted or compressed it). Returns true when sent.
  const uploadAndSend = async (file, caption) => {
    if (sendingRef.current || !onSendMedia) return false;
    const targetPhone = phone; // captured at click time, before the (possibly minutes long) upload
    sendingRef.current = true;
    setSending(true);
    try {
      let uploaded;
      try {
        uploaded = await adminWhatsAppService.uploadMedia(file);
      } catch (err) {
        toast.error(err?.message || 'Could not upload that file');
        return false;
      }
      const check = checkWhatsAppMedia(`x.${uploaded.format}`, uploaded.size);
      if (check.ok === false) {
        toast.error(check.message);
        return false;
      }
      const trimmed = (caption || '').trim();
      const body = { url: uploaded.url, mime_type: check.mime };
      if (trimmed && check.kind !== 'audio') body.caption = trimmed;
      if (check.kind === 'document') body.filename = file.name;
      const result = await onSendMedia(body, targetPhone);
      if (result.ok) return true;
      if (!result.code) toast.error(result.message || 'Could not send that file');
      return false;
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setSending(false);
    }
  };

  const sendPreview = async (caption) => {
    if (!pending || pending.busy || sendingRef.current) return;
    setPending((p) => (p ? { ...p, busy: 'sending' } : p));
    const ok = await uploadAndSend(pending.file, caption);
    if (!mountedRef.current) return;
    if (ok) setPending(null);
    else setPending((p) => (p ? { ...p, busy: null } : p));
  };

  // A failed voice note reopens in the audio preview so the take is never lost.
  const sendVoice = async (file) => {
    if (!file) {
      if (mountedRef.current) setVoiceBusy(false);
      return;
    }
    setVoiceBusy(true);
    const ok = await uploadAndSend(file, '');
    if (!mountedRef.current) return;
    setVoiceBusy(false);
    if (!ok) setPending({ file, serverCompress: false, busy: null });
  };

  // At the 10 minute cap: keep the take and let the admin decide.
  const onLimit = (file) => {
    if (!mountedRef.current || !file) return;
    toast.error('Recording stopped at 10:00. Review it, then send or close.');
    setPending({ file, serverCompress: false, busy: null });
  };

  const recorder = useVoiceRecorder({ onError: (m) => toast.error(m), onAutoStop: onLimit });

  const canCompose = windowOpen && !suppressed && !sendDisabled;
  useEffect(() => {
    if (!canCompose) {
      recorder.cancel();
      setPending(null);
      setMenuOpen(false);
      setInteractiveMode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCompose]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    setMenuOpen(false);
    if (!file) return;
    const pickId = ++pickIdRef.current;
    setPending({ file, serverCompress: false, busy: 'compressing' });
    const prepared = await prepareForWhatsApp(file);
    if (!mountedRef.current || pickId !== pickIdRef.current) return; // closed while compressing
    if (prepared.ok === false) {
      setPending(null);
      toast.error(prepared.message);
      return;
    }
    setPending({ file: prepared.file, serverCompress: prepared.serverCompress, busy: null });
  };

  const submit = async () => {
    if (sendingRef.current) return;
    const text = value.trim();
    if (!text || text.length > MAX_REPLY_CHARS) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const result = editing ? await onSaveEdit(text) : await onSend(text);
      if (result.ok) {
        setValue('');
      } else if (!result.code) {
        // window_closed / suppressed / send_disabled flip the bar itself; anything else is a raw error.
        toast.error(result.message || (editing ? 'Could not save that edit' : 'Could not send that message'));
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  if (sendDisabled) return <DisabledBar text="Sending is switched off on this environment." />;
  if (suppressed) return <DisabledBar text="This person opted out. Messaging is disabled." />;
  if (!windowOpen) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm text-gray-600">Window closed. The customer must message you first, or send a template.</p>
        <button
          type="button"
          onClick={onOpenTemplates}
          className="shrink-0 px-3 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
        >
          Send a template
        </button>
      </div>
    );
  }

  const submitCatalog = async () => {
    const text = catalogBody.trim();
    if (!text) {
      setCatalogError('Write a message first');
      return;
    }
    setCatalogError('');
    setSending(true);
    const result = await onSendCatalog(text);
    setSending(false);
    if (result.ok) {
      setCatalogBody('');
      setInteractiveMode(null);
    } else if (!result.code) {
      setCatalogError(result.message || 'Could not send the catalog');
    }
  };

  const overLimit = value.length > MAX_REPLY_CHARS;
  const showMic = !editing && !!onSendMedia && !value.trim();
  const inVoice = recorder.recording || voiceBusy;
  return (
    <div className="border-t border-gray-200 bg-[#F0F2F5] px-3 py-2">
      {editing && !interactiveMode && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-black/5 px-3 py-2 text-xs text-gray-700">
          <span className="min-w-0 truncate">Editing message</span>
          <button
            type="button"
            aria-label="Cancel edit"
            onClick={onCancelEdit}
            className="shrink-0 text-gray-500 hover:text-gray-800"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {replyingTo && !interactiveMode && !editing && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-black/5 px-3 py-2 text-xs text-gray-700">
          <span className="min-w-0 truncate">Replying to: {replyingTo.text || 'a message'}</span>
          <button
            type="button"
            aria-label="Cancel reply"
            onClick={onCancelReply}
            className="shrink-0 text-gray-500 hover:text-gray-800"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {pending && (
        <MediaPreview
          file={pending.file}
          serverCompress={pending.serverCompress}
          busy={pending.busy}
          onClose={() => {
            pickIdRef.current += 1;
            setPending(null);
          }}
          onSend={sendPreview}
        />
      )}
      {interactiveMode === 'catalog' ? (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-800">Send catalog</p>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setInteractiveMode(null)}
              className="text-gray-500 hover:text-gray-800"
            >
              <X size={16} />
            </button>
          </div>
          <textarea
            aria-label="Catalog message"
            value={catalogBody}
            onChange={(e) => setCatalogBody(e.target.value)}
            rows={2}
            placeholder="Browse our new arrivals"
            className="mb-2 w-full resize-none rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none"
          />
          {catalogError && <p className="mt-1 text-xs text-red-600">{catalogError}</p>}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              disabled={sending}
              onClick={submitCatalog}
              className="rounded-lg bg-[#008069] px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      ) : interactiveMode ? (
        <InteractiveComposer kind={interactiveMode} onSend={onSendInteractive} onCancel={() => setInteractiveMode(null)} />
      ) : inVoice ? (
        <VoiceRecorderBar
          seconds={recorder.seconds}
          busy={voiceBusy}
          onCancel={recorder.cancel}
          onSend={async () => {
            setVoiceBusy(true);
            sendVoice(await recorder.stop());
          }}
        />
      ) : (
        <div className="flex items-end gap-2">
          {!editing && (onSendMedia || onSendInteractive || onSendCatalog) && (
            <div ref={menuRef} className="relative shrink-0">
              <button
                type="button"
                aria-label="Attach file"
                aria-expanded={menuOpen}
                disabled={sending}
                onClick={() => setMenuOpen((open) => !open)}
                className="p-2 text-gray-600 disabled:opacity-40"
              >
                <Paperclip size={20} />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  aria-label="Attach"
                  className="absolute bottom-full left-0 z-10 mb-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                >
                  {onSendMedia &&
                    MENU.map(({ key, label, Icon }) => (
                      <button
                        key={key}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          inputRefs.current[key]?.click();
                        }}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Icon size={18} className="text-[#008069]" /> {label}
                      </button>
                    ))}
                  {onSendMedia && (onSendInteractive || onSendCatalog) && <div className="my-1 border-t border-gray-100" />}
                  {INTERACTIVE_MENU.filter(({ key }) => (key === 'catalog' ? onSendCatalog : onSendInteractive)).map(
                    ({ key, label, Icon }) => (
                      <button
                        key={key}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          setInteractiveMode(key);
                        }}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Icon size={18} className="text-[#008069]" /> {label}
                      </button>
                    ),
                  )}
                </div>
              )}
              {onSendMedia &&
                MENU.map(({ key, label, accept }) => (
                  <input
                    key={key}
                    type="file"
                    data-attach={key}
                    ref={(el) => {
                      inputRefs.current[key] = el;
                    }}
                    aria-label={`${label} file`}
                    accept={accept}
                    onChange={onPick}
                    className="hidden"
                  />
                ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            aria-label="Type a message"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            className="flex-1 resize-none overflow-y-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none"
          />
          {showMic ? (
            <button
              type="button"
              aria-label="Record voice note"
              disabled={sending}
              onClick={() => recorder.start()}
              className="shrink-0 rounded-full bg-[#008069] p-2 text-white disabled:opacity-40"
            >
              <Mic size={18} />
            </button>
          ) : (
            <button
              type="button"
              aria-label={editing ? 'Save edit' : 'Send message'}
              disabled={sending || !value.trim() || overLimit}
              onClick={submit}
              className="shrink-0 rounded-full bg-[#008069] p-2 text-white disabled:opacity-40"
            >
              <Send size={18} />
            </button>
          )}
        </div>
      )}
      {value.length > WARN_AT && (
        <p className={`mt-1 text-right text-xs ${overLimit ? 'text-red-600' : 'text-gray-500'}`}>
          {value.length}/{MAX_REPLY_CHARS}
        </p>
      )}
    </div>
  );
};

export default Composer;
